//@ts-check
import { useCallback, useEffect, useRef, useState } from 'react'
import { assoc, assocPath, lensPath, set } from 'ramda'
import { classroomTabIds } from 'components/academy/classroom/student/Tabs'
import { classroomToolbarIds } from 'components/academy/classroom/Toolbar'
import {
  hangUpCall,
  sendVideoRemove,
  sendVideoSelect,
  sendSelectedTabChange,
  sendNotebookChange,
  sendImageSelect,
  sendNotebookSyncPage
} from 'api/sockets'
import useClassroomStorage, {
  getStoredDataByStudent
} from './useClassroomStorage'

const initialSelectedToolState = {
  [classroomTabIds.blackboard]: classroomToolbarIds.draw,
  [classroomTabIds.notebook]: classroomToolbarIds.draw,
  [classroomTabIds.photo]: classroomToolbarIds.draw,
  [classroomTabIds.video]: classroomToolbarIds.draw
}

const initialZoomState = {
  [classroomTabIds.blackboard]: 0,
  [classroomTabIds.notebook]: 0,
  [classroomTabIds.photo]: 0,
  [classroomTabIds.video]: 0
}

const initialCanvasState = {
  [classroomTabIds.blackboard]: [],
  [classroomTabIds.notebook]: [],
  [classroomTabIds.photo]: [],
  [classroomTabIds.video]: []
}

const initialState = {
  selectedTabByStudent: classroomTabIds.blackboard,
  selectedTab: classroomTabIds.blackboard,
  toolBySection: initialSelectedToolState,
  zoomBySection: initialZoomState,
  canvasBySection: initialCanvasState,
  learningVideo: '',
  selectedVideoSubjectId: '',
  selectedNotebookData: null,
  selectedPhotoData: null,
  subjectLevel: null,
  subjectSublevel: null
}

function makeInitialState(
  studentId,
  initialSubjectId,
  initialSubjectLevel,
  initialSubjectSublevel
) {
  const storedData = getStoredDataByStudent(studentId)
  return {
    ...initialState,
    subjectLevel: initialSubjectLevel ?? storedData?.subjectLevel ?? null,
    subjectSublevel:
      initialSubjectSublevel ?? storedData?.subjectSublevel ?? null,
    learningVideo: storedData?.learningVideo,
    selectedVideoSubjectId:
      storedData?.selectedVideoSubjectId || initialSubjectId,
    selectedPhotoData: storedData?.selectedPhotoData?.id
      ? {
          id: storedData?.selectedPhotoData?.id,
          location: storedData?.selectedPhotoData?.location,
          analysis: storedData?.selectedPhotoData?.analysis,
          rating: storedData?.selectedPhotoData?.rating
        }
      : null,
    selectedNotebookData: storedData?.selectedNotebookData?.url
      ? {
          subjectId:
            storedData?.selectedNotebookData?.subjectId || initialSubjectId,
          url: storedData?.selectedNotebookData?.url,
          title: storedData?.selectedNotebookData?.title,
          page: storedData?.selectedNotebookData?.page
        }
      : {
          subjectId: initialSubjectId,
          url: '',
          title: '',
          page: 1
        }
  }
}

export default function useStudentClassroom({
  socket,
  student,
  isActive = false,
  initialSubjectId = '',
  initialSubjectLevel = null,
  initialSubjectSublevel = null,
  teacherIsReconnecting = false
}) {
  const [state, setState] = useState(
    makeInitialState(
      student,
      initialSubjectId,
      initialSubjectLevel,
      initialSubjectSublevel
    )
  )
  const classroomStorage = useClassroomStorage()
  const hasRestoredData = useRef(false)
  const {
    selectedTabByStudent,
    selectedTab,
    toolBySection,
    zoomBySection,
    canvasBySection,
    learningVideo,
    selectedVideoSubjectId,
    selectedNotebookData,
    selectedPhotoData,
    subjectLevel,
    subjectSublevel
  } = state

  const handleTabChange = tabId => {
    setState(assoc('selectedTab', tabId))
    sendSelectedTabChange({ socket, tabId, student }).catch(_ => null)
  }

  const handleToolChange = toolId =>
    setState(assocPath(['toolBySection', selectedTab], toolId))

  const handleZoomChange = value =>
    setState(assocPath(['zoomBySection', selectedTab], value))

  const handleSaveCanvasContext = useCallback((partialContext, tab) => {
    const tabLens = lensPath(['canvasBySection', tab])
    setState(state => set(tabLens, partialContext, state))
  }, [])
  const handleClearCanvasContext = useCallback(tab => {
    const tabLens = lensPath(['canvasBySection', tab])
    setState(set(tabLens, []))
  }, [])

  const handleLearningVideoChange = useCallback(
    (learningVideo = '', selectedVideoSubjectId = '') => {
      if (learningVideo)
        sendVideoSelect({ socket, student, url: learningVideo }).catch(
          _ => null
        )
      else sendVideoRemove({ socket, student }).catch(_ => null)
      setState(assoc('learningVideo', learningVideo))
      setState(assoc('selectedVideoSubjectId', selectedVideoSubjectId))
      classroomStorage.updateStudentData(student, {
        learningVideo,
        selectedVideoSubjectId
      })
    },
    [socket, student, classroomStorage]
  )

  const handleHangUpCall = useCallback(() => {
    hangUpCall({ socket, student }).catch(_ => null)
  }, [socket, student])

  const handleNotebookChange = useCallback(
    selectedNotebookData => {
      setState(assoc('selectedNotebookData', selectedNotebookData))
      classroomStorage.updateStudentData(student, {
        selectedNotebookData
      })
    },
    [student, classroomStorage]
  )

  const handlePhotoChange = useCallback(
    selectedPhotoData => {
      setState(assoc('selectedPhotoData', selectedPhotoData))
      classroomStorage.updateStudentData(student, {
        selectedPhotoData
      })
    },
    [student, classroomStorage]
  )

  useEffect(() => {
    if (teacherIsReconnecting)
      setState(assoc('selectedTab', classroomTabIds.none))
  }, [teacherIsReconnecting])

  useEffect(() => {
    if (!socket) return
    socket.on('classroom:students:selected-tab', ({ studentId, tabId }) => {
      if (studentId !== student) return
      setState(assoc('selectedTabByStudent', tabId))
    })
    return () => {
      if (!socket || !isActive) return
      socket.off('classroom:students:selected-tab')
    }
  }, [socket, student, isActive])

  useEffect(() => {
    if (!socket || hasRestoredData.current) return

    hasRestoredData.current = true

    if (learningVideo)
      sendVideoSelect({
        socket,
        student,
        url: learningVideo
      }).catch(_ => null)
    if (selectedNotebookData?.url) {
      sendNotebookChange({
        socket,
        student,
        url: selectedNotebookData.url,
        title: selectedNotebookData.title
      })
        .then(() => {
          setTimeout(() => {
            sendNotebookSyncPage({
              socket,
              student,
              page: selectedNotebookData.page || 1
            }).catch(_ => null)
          }, 750)
        })
        .catch(_ => null)
    }
    if (selectedPhotoData?.id) {
      sendImageSelect({
        socket,
        student,
        imageId: selectedPhotoData.id,
        imageLocation: selectedPhotoData.location,
        imageAnalysis: selectedPhotoData.analysis,
        imageRating: selectedPhotoData.rating
      }).catch(_ => null)
    }
  }, [
    socket,
    student,
    learningVideo,
    selectedNotebookData,
    selectedPhotoData,
    handleLearningVideoChange,
    handleNotebookChange,
    handlePhotoChange
  ])

  return {
    isActive,
    selectedTabByStudent,
    selectedTab,
    selectedTool: toolBySection[selectedTab],
    zoom: zoomBySection[selectedTab],
    canvasBySection,
    learningVideo,
    selectedVideoSubjectId,
    selectedNotebookData,
    selectedPhotoData,
    subjectLevel,
    subjectSublevel,
    handleTabChange,
    handleToolChange,
    handleZoomChange,
    handleSaveCanvasContext,
    handleClearCanvasContext,
    handleLearningVideoChange,
    handleHangUpCall,
    handleNotebookChange,
    handlePhotoChange
  }
}
